import { z } from "zod";
import { CookingSkillSchema } from "./profile.js";
import { NutritionTotalsSchema } from "./plans.js";
/**
 * GET /v1/recipes/{id} — recipe detail (live-review gap: nothing exposed
 * steps/ingredients/allergens/nutrition for a single recipe; the plan surface
 * only ever carried recipeId + a computed per-meal nutrition total).
 */
// The 14 EU FIC Annex II allergen categories — mirrors
// services/planning/src/validator/types.ts's EU_ALLERGENS exactly (that
// module is the source of truth; this is the wire-contract copy).
export const EuAllergenSchema = z.enum([
    "gluten",
    "crustaceans",
    "eggs",
    "fish",
    "peanuts",
    "soybeans",
    "milk",
    "nuts",
    "celery",
    "mustard",
    "sesame",
    "sulphites",
    "lupin",
    "molluscs",
]);
/**
 * 'none' is deliberately excluded from the wire vocabulary: this field is an
 * aggregated WORST CASE across every ingredient in the recipe (ADR-012 §1
 * fail-closed relations), and only allergens the recipe could plausibly
 * contain are ever listed — a 'none' entry would carry no information the
 * absence of a list entry doesn't already carry.
 */
export const RecipeAllergenRelationSchema = z.enum(["contains", "may_contain", "unknown"]);
export const RecipeAllergenEntrySchema = z.object({
    allergen: EuAllergenSchema,
    relation: RecipeAllergenRelationSchema,
});
export const RecipeIngredientLineSchema = z.object({
    ingredientId: z.string(),
    displayName: z.object({ de: z.string(), en: z.string() }),
    quantity: z.number(),
    unit: z.string(),
});
/**
 * curated = services/catalog seed data; amy_generated = a recipe Amy composed
 * that passed the deterministic validator and was persisted (ADR-013
 * Decision 1; services/db/migrations/0008_health_conditions_provenance.sql).
 */
export const RecipeProvenanceSchema = z.enum(["curated", "amy_generated"]);
export const RecipeDetailResponseSchema = z.object({
    id: z.string(),
    title: z.object({ de: z.string(), en: z.string() }),
    description: z.object({ de: z.string(), en: z.string() }),
    steps: z.object({ de: z.array(z.string()), en: z.array(z.string()) }),
    activeMinutes: z.number().int().nonnegative(),
    totalMinutes: z.number().int().nonnegative(),
    servingsDefault: z.number().int().positive(),
    skill: CookingSkillSchema,
    ingredients: z.array(RecipeIngredientLineSchema),
    allergens: z.array(RecipeAllergenEntrySchema),
    /** Null only when composition is incomplete for one or more ingredients (fail-closed — never estimated/guessed). */
    nutritionPerServing: NutritionTotalsSchema.nullable(),
    provenance: RecipeProvenanceSchema,
    /** Static disclosure copy — never a per-recipe confidence claim. */
    sourceNote: z.string(),
    /**
     * ADR-077 Category C: a declared-generic decorative image tag (e.g.
     * "dinner/fish"), resolved client-side to a bundled stock-photo asset via
     * a fallback-tree walk — never a claim that this photo depicts this exact
     * dish (that remains prohibited — ADR-070 Category A). Null for a recipe
     * with no computed tag (resolves through the client's own "generic"
     * fallback, which always renders something).
     */
    imageTag: z.string().nullable(),
    /**
     * Portion truth (owner decision 2026-09-04): when the request names the
     * user's planned portion (`?servings=1.5`), the SERVER scales the recipe
     * to it — ingredient quantities the user is expected to cook/eat and the
     * nutrition of that portion — so the header, the ingredient list and the
     * nutrition block describe ONE portion. `nutritionPerServing` +
     * `servingsDefault` remain the base-recipe facts (one recipe serving).
     * Absent when no portion was requested. Never household size.
     */
    portion: z
        .object({
        servings: z.number().positive(),
        servingsDefault: z.number().int().positive(),
        ingredients: z.array(RecipeIngredientLineSchema),
        nutrition: NutritionTotalsSchema.nullable(),
    })
        .optional(),
});
/**
 * GET /v1/recipes — catalog-wide recipe search (Launch prep, 2026-09-24):
 * the missing half of the gap `search_recipes`'s own tool docstring
 * documented ("no standalone recipe-catalog search endpoint yet... searches
 * the meals already on the user's current plan"). Lets a caller resolve an
 * EXISTING BiteBalance recipe by name to its id even when it is not
 * currently scheduled on the user's plan, so it can be applied via
 * `POST /v1/plans/{id}/proposals` with `targetRecipeId` instead of being
 * recomposed from scratch. Read-only, case-insensitive substring match
 * against title (German or English); an absent/empty `q` returns the
 * catalog from the top, capped by `limit`. Deliberately minimal (id, title,
 * timing) — nutrition/ingredients/allergens are the detail endpoint's job,
 * surfaced naturally by the proposal preview once a recipeId is chosen.
 */
export const RecipeCatalogSearchQuerySchema = z.object({
    q: z.string().max(200).optional(),
    limit: z.coerce.number().int().positive().max(50).optional(),
});
export const RecipeCatalogSearchResultSchema = z.object({
    id: z.string(),
    title: z.object({ de: z.string(), en: z.string() }),
    activeMinutes: z.number().int().nonnegative(),
    totalMinutes: z.number().int().nonnegative(),
    servingsDefault: z.number().int().positive(),
    provenance: RecipeProvenanceSchema,
});
export const RecipeCatalogSearchResponseSchema = z.object({
    recipes: z.array(RecipeCatalogSearchResultSchema),
});
//# sourceMappingURL=recipes.js.map